import React, { useEffect, useState, useMemo } from 'react';
import {
  Users,
  GraduationCap,
  UserCog,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Clock,
  Search,
  Filter,
  X,
  Check,
  FileSpreadsheet,
  FileText,
  Loader2,
  Eye,
  FileInput,
  BookOpen,
} from 'lucide-react';
import {
  getEdepAcademicYears,
  EdepAcademicYear,
  getEdepReportPreview,
  downloadEdepExcel,
  downloadEdepPdf,
  EdepSummary,
  EdepReportType,
  EdepReportResponse,
  StudentProfileReport,
  StaffProfileReport,
  TeacherProfileReport,
} from '../../reports/services/edepReportService';
import { fetchActiveCampuses, CampusOption } from '@/services/userService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { Permission } from '@/types/global.types';
import { getAuthHeaders, API_BASE } from '@/utils/api';

// Helper to check if diplome is BAC or similar
const isBacDiplome = (diplome?: string | null): boolean => {
  if (!diplome) return true; // Default to BAC if not specified
  const normalized = diplome.toLowerCase().trim();
  return normalized === 'bac' ||
         normalized === 'baccalauréat' ||
         normalized === 'baccalaureat' ||
         normalized.startsWith('bac ') ||
         normalized.includes('baccalaur');
};

type TabType = 'profile' | 'physical';
type UserCategoryType = 'STUDENTS' | 'STAFF' | 'TEACHERS';

export const SuiviDossiersPage: React.FC = () => {
  // Get auth state reactively to ensure we wait for hydration
  const { user, tokens, isAuthenticated, hasPermission } = useAuthStore();

  // Permission checks
  const canViewProfiles = hasPermission(Permission.TRACKING_PROFILE_VIEW);
  const canExportProfiles = hasPermission(Permission.TRACKING_PROFILE_EXPORT);
  const canViewPhysicalDocs = hasPermission(Permission.TRACKING_PHYSICAL_DOCS_VIEW);
  const canExportPhysicalDocs = hasPermission(Permission.TRACKING_PHYSICAL_DOCS_EXPORT);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  // Common state
  const [summary, setSummary] = useState<EdepSummary | null>(null);
  const [campuses, setCampuses] = useState<CampusOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile tab state - store reports for all categories
  const [studentsReport, setStudentsReport] = useState<EdepReportResponse | null>(null);
  const [staffReport, setStaffReport] = useState<EdepReportResponse | null>(null);
  const [teachersReport, setTeachersReport] = useState<EdepReportResponse | null>(null);
  const [loadingTable, setLoadingTable] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<UserCategoryType>('STUDENTS');
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');
  // The academic year being consulted. Shared by both tabs: the scanned and the
  // physical file of a year describe the same people, so they must never show
  // two different years side by side.
  const [academicYears, setAcademicYears] = useState<EdepAcademicYear[]>([]);
  const [selectedYearId, setSelectedYearId] = useState<string>('');
  const [currentYearId, setCurrentYearId] = useState<string | null>(null);
  // Set when the calendar parameters say we are in a year the institution has
  // not created yet — the page is then showing the previous one.
  const [missingCurrentYear, setMissingCurrentYear] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Physical documents tab state
  const [physicalDocsCategory, setPhysicalDocsCategory] = useState<'STUDENTS' | 'STAFF' | 'TEACHERS'>('STUDENTS');
  const [physicalDocsData, setPhysicalDocsData] = useState<any[]>([]);
  const [loadingPhysicalDocs, setLoadingPhysicalDocs] = useState(false);
  const [physicalDocsSearch, setPhysicalDocsSearch] = useState('');
  const [updatingDocId, setUpdatingDocId] = useState<string | null>(null);
  const [exportingPhysicalExcel, setExportingPhysicalExcel] = useState(false);
  const [exportingPhysicalPdf, setExportingPhysicalPdf] = useState(false);
  const [showCompletePhysicalDocs, setShowCompletePhysicalDocs] = useState(false);
  const [physicalDocsSummary, setPhysicalDocsSummary] = useState<{
    students: { total: number; complete: number; incomplete: number; completePercentage: number };
    staff: { total: number; complete: number; incomplete: number; completePercentage: number };
    teachers: { total: number; complete: number; incomplete: number; completePercentage: number };
  } | null>(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Load initial data - wait for auth to be ready (hydration from localStorage)
  useEffect(() => {
    // Only load data when auth is ready (user and tokens available)
    if (isAuthenticated && user && tokens?.accessToken) {
      loadInitialData();
    }
  }, [isAuthenticated, user, tokens?.accessToken]);

  // Reload all reports when campus filter changes (for consistency)
  useEffect(() => {
    if (initialDataLoaded) {
      loadAllReports();
    }
  }, [selectedCampusId, selectedYearId, initialDataLoaded]);

  // Load report for selected category when category changes
  useEffect(() => {
    if (initialDataLoaded && activeTab === 'profile') {
      loadReportData();
    }
  }, [selectedCategory, activeTab, initialDataLoaded]);

  // Load physical documents data when tab or category changes
  useEffect(() => {
    if (initialDataLoaded && activeTab === 'physical') {
      loadPhysicalDocsData();
      loadPhysicalDocsSummary();
    }
  }, [physicalDocsCategory, selectedCampusId, selectedYearId, activeTab, initialDataLoaded]);

  const selectedYearName = academicYears.find((y) => y.id === selectedYearId)?.name ?? null;
  // A past year is read-only history; the year in progress is the one being chased.
  const isViewingCurrentYear = !selectedYearId || selectedYearId === currentYearId;

  // Helper to calculate summary from loaded report data
  const calculateSummaryFromReports = (
    students: EdepReportResponse | null,
    staff: EdepReportResponse | null,
    teachers: EdepReportResponse | null
  ) => {
    const studentStats = students?.stats || { total: 0, complete: 0, incomplete: 0, completePercentage: 0, incompletePercentage: 0, byField: [] };
    const staffStats = staff?.stats || { total: 0, complete: 0, incomplete: 0, completePercentage: 0, incompletePercentage: 0, byField: [] };
    const teacherStats = teachers?.stats || { total: 0, complete: 0, incomplete: 0, completePercentage: 0, incompletePercentage: 0, byField: [] };

    return {
      students: studentStats,
      staff: staffStats,
      teachers: teacherStats,
      generatedAt: new Date().toISOString(),
    };
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load campuses and academic years first: every report below is read for
      // one year, so the year has to be known before anything is fetched.
      const [campusesData, yearsData] = await Promise.all([
        fetchActiveCampuses(),
        getEdepAcademicYears(),
      ]);
      setCampuses(campusesData.campuses);
      setAcademicYears(yearsData.years);
      setCurrentYearId(yearsData.currentYearId);

      // Default to the year in progress, derived from the institution's calendar
      // parameters (Paramètres → gestion de planning).
      const yearId = selectedYearId || yearsData.currentYearId || yearsData.years[0]?.id || '';
      setSelectedYearId(yearId);

      // Warn when the calendar says we are in a year that does not exist yet:
      // without this the page silently reports the previous year as if current.
      const digits = (value: string): string => value.replace(/[^0-9]/g, '');
      setMissingCurrentYear(
        yearsData.currentYearName &&
          digits(yearsData.currentYearName) !== digits(yearsData.expectedCurrentYearName)
          ? yearsData.expectedCurrentYearName
          : null,
      );

      // Load all three category reports in parallel for consistency
      const [studentsData, staffData, teachersData] = await Promise.all([
        getEdepReportPreview('STUDENTS', selectedCampusId || undefined, yearId || undefined),
        getEdepReportPreview('STAFF', selectedCampusId || undefined, yearId || undefined),
        getEdepReportPreview('TEACHERS', selectedCampusId || undefined, yearId || undefined),
      ]);

      setStudentsReport(studentsData);
      setStaffReport(staffData);
      setTeachersReport(teachersData);

      // Calculate summary from actual report data
      const calculatedSummary = calculateSummaryFromReports(studentsData, staffData, teachersData);
      setSummary(calculatedSummary as EdepSummary);

      setInitialDataLoaded(true);

      // Load physical docs data if on physical tab
      if (activeTab === 'physical') {
        await loadPhysicalDocsData();
        await loadPhysicalDocsSummary();
      }
    } catch (err) {
      setError('Impossible de charger les donnees');
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllReports = async () => {
    try {
      setLoadingTable(true);
      setError(null);

      // Load all three category reports in parallel
      const [studentsData, staffData, teachersData] = await Promise.all([
        getEdepReportPreview('STUDENTS', selectedCampusId || undefined, selectedYearId || undefined),
        getEdepReportPreview('STAFF', selectedCampusId || undefined, selectedYearId || undefined),
        getEdepReportPreview('TEACHERS', selectedCampusId || undefined, selectedYearId || undefined),
      ]);

      setStudentsReport(studentsData);
      setStaffReport(staffData);
      setTeachersReport(teachersData);

      // Calculate summary from all reports
      const calculatedSummary = calculateSummaryFromReports(studentsData, staffData, teachersData);
      setSummary(calculatedSummary as EdepSummary);
    } catch (err) {
      console.error('Error loading all reports:', err);
      setError('Impossible de charger les donnees des profils');
    } finally {
      setLoadingTable(false);
    }
  };

  const loadReportData = async () => {
    try {
      setLoadingTable(true);
      setError(null);
      const reportType: EdepReportType = selectedCategory === 'STUDENTS' ? 'STUDENTS' :
                                          selectedCategory === 'TEACHERS' ? 'TEACHERS' : 'STAFF';
      const data = await getEdepReportPreview(
        reportType,
        selectedCampusId || undefined,
        selectedYearId || undefined
      );

      // Update the appropriate report state
      if (selectedCategory === 'STUDENTS') {
        setStudentsReport(data);
      } else if (selectedCategory === 'TEACHERS') {
        setTeachersReport(data);
      } else {
        setStaffReport(data);
      }

      // Recalculate summary with updated report
      const updatedSummary = calculateSummaryFromReports(
        selectedCategory === 'STUDENTS' ? data : studentsReport,
        selectedCategory === 'STAFF' ? data : staffReport,
        selectedCategory === 'TEACHERS' ? data : teachersReport
      );
      setSummary(updatedSummary as EdepSummary);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError('Impossible de charger les donnees des profils');
    } finally {
      setLoadingTable(false);
    }
  };

  // Load physical documents tracking data - uses E-DEP data source for consistency
  const loadPhysicalDocsData = async () => {
    try {
      setLoadingPhysicalDocs(true);
      setError(null);

      // Use E-DEP report preview to get consistent data (same as profile tab)
      const reportType: EdepReportType = physicalDocsCategory === 'STUDENTS' ? 'STUDENTS' :
                                          physicalDocsCategory === 'TEACHERS' ? 'TEACHERS' : 'STAFF';
      const edepData = await getEdepReportPreview(
        reportType,
        selectedCampusId || undefined,
        selectedYearId || undefined
      );

      // Transform E-DEP data to physical docs format
      const transformed = (edepData.data as any[]).map((item: any) => {
        const physicalDocs = item.physicalDocs || {};

        // Define required docs based on category
        let docFields: { key: string; profileField: string; required: boolean }[];
        if (physicalDocsCategory === 'STUDENTS') {
          const studentItem = item as StudentProfileReport;
          // Use helper to check for BAC variations (Baccalauréat, Baccalaureat, BAC, etc.)
          const hasNonBacDiplome = !isBacDiplome(studentItem.diplomeAcces);
          // Previous years transcripts only needed for TRANSFER students
          // (enrolled this year directly into year 2+, meaning they did previous years elsewhere)
          const isTransferStudent = (studentItem.niveauFormationInitial ?? 1) > 1;
          const needsPreviousYearsTranscripts = isTransferStudent;

          docFields = [
            // Required documents
            { key: 'CIN', profileField: 'physicalDocStudentCin', required: true },
            { key: 'BAC', profileField: 'physicalDocStudentBac', required: true },
            { key: 'RELEVE_BAC', profileField: 'physicalDocStudentReleveBac', required: true },
          ];

          // Conditional: Non-BAC diploma
          if (hasNonBacDiplome) {
            docFields.push(
              { key: 'DERNIER_DIPLOME', profileField: 'physicalDocStudentDernierDiplome', required: true },
              { key: 'RELEVE_DIPLOME', profileField: 'physicalDocStudentReleveDiplome', required: true },
            );
          }

          // Conditional: Previous years transcripts
          if (needsPreviousYearsTranscripts) {
            docFields.push(
              { key: 'RELEVE_ANNEES_PREC', profileField: 'physicalDocStudentReleveAnneesPrecedentes', required: true },
            );
          }

          // Optional documents (don't count toward completion)
          docFields.push(
            { key: 'CV', profileField: 'physicalDocStudentCv', required: false },
            { key: 'LETTRE_MOTIVATION', profileField: 'physicalDocStudentLettreMotivation', required: false },
          );
        } else if (physicalDocsCategory === 'TEACHERS') {
          docFields = [
            // CIN_FRONT column = identity requirement, satisfied by CIN OR Passeport (see below)
            { key: 'CIN_FRONT', profileField: 'physicalDocCin', required: true },
            { key: 'REGLEMENT_INTERIEUR_SIGNED', profileField: 'physicalDocReglementInterieur', required: true },
            { key: 'CONSENTEMENT_SIGNED', profileField: 'physicalDocConsentement', required: true },
            { key: 'DIPLOME_SCAN', profileField: 'physicalDocDiplome', required: true },
            { key: 'CV', profileField: 'physicalDocCv', required: true },
            { key: 'RIB', profileField: 'physicalDocAttestationRib', required: true },
            { key: 'CONTRAT_TRAVAIL', profileField: 'physicalDocContratTravail', required: true },
            { key: 'ENGAGEMENT_PROFESSEUR', profileField: 'physicalDocEngagementProfesseur', required: true },
          ];
          // Autorisation d'enseigner: only applicable (and required) for vacataires
          // from a public institution — others show "-" in the column
          if (physicalDocs.autorisationRequired === true) {
            docFields.push({ key: 'AUTORISATION_ENSEIGNER', profileField: 'physicalDocAutorisationEnseigner', required: true });
          }
        } else {
          // Staff
          docFields = [
            // CIN_FRONT column = identity requirement, satisfied by CIN OR Passeport (see below)
            { key: 'CIN_FRONT', profileField: 'physicalDocCin', required: true },
            { key: 'REGLEMENT_INTERIEUR_SIGNED', profileField: 'physicalDocReglementInterieur', required: true },
            { key: 'CONSENTEMENT_SIGNED', profileField: 'physicalDocConsentement', required: true },
            { key: 'DIPLOME_SCAN', profileField: 'physicalDocDiplome', required: true },
            { key: 'CV', profileField: 'physicalDocCv', required: true },
            { key: 'RIB', profileField: 'physicalDocAttestationRib', required: true },
            { key: 'CONTRAT_TRAVAIL', profileField: 'physicalDocContratTravail', required: true },
          ];
        }

        // Build documents map from physical docs status
        const documents: Record<string, { isReceived: boolean; label: string; required: boolean }> = {};
        let totalReceived = 0;
        let totalRequiredReceived = 0;
        docFields.forEach(({ key, profileField, required }) => {
          // Identity: CIN OR Passeport satisfies the requirement (never both needed)
          const isReceived = key === 'CIN_FRONT' && physicalDocsCategory !== 'STUDENTS'
            ? physicalDocs.physicalDocCin === true || physicalDocs.physicalDocPassport === true
            : physicalDocs[profileField] === true;
          if (isReceived) {
            totalReceived++;
            if (required) totalRequiredReceived++;
          }
          documents[key] = { isReceived, label: key, required };
        });

        // Only count required documents toward completion
        const requiredDocs = docFields.filter(d => d.required);
        const totalRequired = requiredDocs.length;
        const percentageComplete = totalRequired > 0 ? Math.round((totalRequiredReceived / totalRequired) * 100) : 0;
        const isComplete = percentageComplete === 100;

        // Get code based on category
        const code = physicalDocsCategory === 'STUDENTS'
          ? (item as StudentProfileReport).studentCode
          : (item as StaffProfileReport).staffCode || '';

        return {
          id: item.id,
          // User id for admin update endpoints: student rows carry the Student table
          // PK in `id` and the user id in `userId`; staff/teacher rows use the user id as `id`.
          userId: physicalDocsCategory === 'STUDENTS'
            ? (item as StudentProfileReport).userId
            : item.id,
          userName: `${item.lastName} ${item.firstName}`,
          name: `${item.lastName} ${item.firstName}`,
          firstName: item.firstName,
          lastName: item.lastName,
          userRole: physicalDocsCategory === 'STUDENTS' ? 'STUDENT' : (item as StaffProfileReport).role,
          code,
          email: item.email || '',
          phone: item.phone || '',
          phoneCountry: item.phoneCountry || '+212',
          totalRequired,
          totalReceived: totalRequiredReceived,
          percentageComplete,
          isComplete,
          documents,
          rawDocuments: [],
        };
      });
      setPhysicalDocsData(transformed);
    } catch (err: any) {
      console.error('Error loading physical documents data:', err);
      setError('Impossible de charger les donnees des documents physiques');
      setPhysicalDocsData([]);
    } finally {
      setLoadingPhysicalDocs(false);
    }
  };

  // Load physical documents summary stats for all categories
  // Uses E-DEP data source for consistency with profile tab
  const loadPhysicalDocsSummary = async () => {
    try {
      const categories = ['STUDENTS', 'STAFF', 'TEACHERS'] as const;
      const results: any = {};

      for (const reportType of categories) {
        try {
          const edepData = await getEdepReportPreview(
            reportType,
            selectedCampusId || undefined,
            selectedYearId || undefined
          );

          // Define required docs based on category
          const getDocFields = (category: typeof reportType, item?: any) => {
            if (category === 'STUDENTS') {
              // Student physical docs - base required
              const baseFields = ['physicalDocStudentCin', 'physicalDocStudentBac', 'physicalDocStudentReleveBac'];

              if (item) {
                const studentItem = item as StudentProfileReport;
                // Conditional: Non-BAC diploma (use helper to check BAC variations)
                if (!isBacDiplome(studentItem.diplomeAcces)) {
                  baseFields.push('physicalDocStudentDernierDiplome', 'physicalDocStudentReleveDiplome');
                }
                // Conditional: Previous years transcripts (only for TRANSFER students)
                const isTransfer = (studentItem.niveauFormationInitial ?? 1) > 1;
                if (isTransfer) {
                  baseFields.push('physicalDocStudentReleveAnneesPrecedentes');
                }
              }
              return baseFields;
            } else if (category === 'TEACHERS') {
              const fields = ['physicalDocCin', 'physicalDocReglementInterieur', 'physicalDocConsentement', 'physicalDocDiplome', 'physicalDocCv', 'physicalDocAttestationRib', 'physicalDocContratTravail', 'physicalDocEngagementProfesseur'];
              // Autorisation only counts for vacataires from a public institution
              if (item?.physicalDocs?.autorisationRequired === true) {
                fields.push('physicalDocAutorisationEnseigner');
              }
              return fields;
            } else {
              return ['physicalDocCin', 'physicalDocReglementInterieur', 'physicalDocConsentement', 'physicalDocDiplome', 'physicalDocCv', 'physicalDocAttestationRib', 'physicalDocContratTravail'];
            }
          };

          // Calculate completion stats from physicalDocs fields
          const complete = (edepData.data as any[]).filter((item: any) => {
            const physicalDocs = item.physicalDocs || {};
            const docFields = getDocFields(reportType, item);
            return docFields.every((field) =>
              // Identity for staff/teachers: CIN OR Passeport satisfies it
              field === 'physicalDocCin' && reportType !== 'STUDENTS'
                ? physicalDocs.physicalDocCin === true || physicalDocs.physicalDocPassport === true
                : physicalDocs[field] === true,
            );
          }).length;

          const key = reportType === 'TEACHERS' ? 'teachers' : reportType === 'STUDENTS' ? 'students' : 'staff';
          const total = edepData.stats.total;
          const incomplete = total - complete;
          const completePercentage = total > 0 ? Math.round((complete / total) * 100) : 0;

          results[key] = { total, complete, incomplete, completePercentage };
        } catch {
          // Skip errors for individual categories
        }
      }

      setPhysicalDocsSummary(results as any);
    } catch (err) {
      console.error('Error loading physical docs summary:', err);
    }
  };

  // Toggle physical document received status - updates profile fields
  const togglePhysicalDoc = async (userId: string, docType: string, received: boolean) => {
    try {
      setUpdatingDocId(`${userId}-${docType}`);

      // Map document type columns to profile field names for staff/teachers
      const staffDocTypeToProfileField: Record<string, string> = {
        'CIN_FRONT': 'physicalDocCin',
        'PASSPORT': 'physicalDocPassport',
        'CV': 'physicalDocCv',
        'CONTRAT_TRAVAIL': 'physicalDocContratTravail',
        'DIPLOME_SCAN': 'physicalDocDiplome',
        'RIB': 'physicalDocAttestationRib',
        'ENGAGEMENT_PROFESSEUR': 'physicalDocEngagementProfesseur',
        'REGLEMENT_INTERIEUR_SIGNED': 'physicalDocReglementInterieur',
        'CONSENTEMENT_SIGNED': 'physicalDocConsentement',
        'AUTORISATION_ENSEIGNER': 'physicalDocAutorisationEnseigner',
      };

      // Map document type columns to profile field names for students
      const studentDocTypeToProfileField: Record<string, string> = {
        'CIN': 'physicalDocStudentCin',
        'BAC': 'physicalDocStudentBac',
        'RELEVE_BAC': 'physicalDocStudentReleveBac',
        'DERNIER_DIPLOME': 'physicalDocStudentDernierDiplome',
        'RELEVE_DIPLOME': 'physicalDocStudentReleveDiplome',
        'RELEVE_ANNEES_PREC': 'physicalDocStudentReleveAnneesPrecedentes',
        'CV': 'physicalDocStudentCv',
        'LETTRE_MOTIVATION': 'physicalDocStudentLettreMotivation',
      };

      const isStudent = physicalDocsCategory === 'STUDENTS';
      const docTypeToProfileField = isStudent ? studentDocTypeToProfileField : staffDocTypeToProfileField;

      const profileField = docTypeToProfileField[docType];
      if (!profileField) {
        throw new Error(`Unknown document type: ${docType}`);
      }

      // The professor's ANNUAL paperwork (engagement + autorisation d'enseigner)
      // is recorded per academic year, not on the profile: ticking it must apply
      // to the year being consulted, so a paper received late can be recorded
      // against the year it covers without altering any other year.
      const isAnnualProfessorDoc =
        !isStudent &&
        (docType === 'ENGAGEMENT_PROFESSEUR' || docType === 'AUTORISATION_ENSEIGNER');

      if (isAnnualProfessorDoc) {
        const yearName = academicYears.find((y) => y.id === selectedYearId)?.name;
        const params = new URLSearchParams();
        if (yearName) params.set('academicYearLabel', yearName);

        const url =
          docType === 'ENGAGEMENT_PROFESSEUR'
            ? `${API_BASE}/professor-engagement/professor/${userId}/${
                received ? 'physical-doc-received' : 'physical-doc-unreceived'
              }?${params.toString()}`
            : `${API_BASE}/professor-engagement/professor/${userId}/autorisation-physical-doc?${(() => {
                params.set('received', String(received));
                return params.toString();
              })()}`;

        const response = await fetch(url, {
          method: 'PATCH',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error('Failed to update annual document status');
        }
      } else {
        // Update the profile field via the appropriate admin profile endpoint
        const updatePayload: Record<string, boolean> = {};
        updatePayload[profileField] = received;

        // The identity column is "CIN ou Passeport": unchecking must clear both
        // flags, otherwise a set passport flag keeps the requirement satisfied
        if (!isStudent && docType === 'CIN_FRONT' && !received) {
          updatePayload.physicalDocCin = false;
          updatePayload.physicalDocPassport = false;
        }

        const endpoint = isStudent
          ? `${API_BASE}/profile/admin/students/${userId}`
          : `${API_BASE}/profile/admin/staff/${userId}`;

        const response = await fetch(endpoint, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(updatePayload),
        });

        if (!response.ok) {
          throw new Error('Failed to update profile');
        }
      }

      await loadPhysicalDocsData();
      await loadPhysicalDocsSummary();
      setSuccess('Document mis a jour avec succes');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error toggling physical document:', err);
      setError('Erreur lors de la mise a jour du document');
    } finally {
      setUpdatingDocId(null);
    }
  };

  const handleRefresh = async () => {
    if (activeTab === 'profile') {
      await loadInitialData();
    } else {
      await Promise.all([loadPhysicalDocsData(), loadPhysicalDocsSummary()]);
    }
    setSuccess('Donnees actualisees');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      const reportType: EdepReportType = selectedCategory === 'STUDENTS' ? 'STUDENTS' :
                                          selectedCategory === 'TEACHERS' ? 'TEACHERS' : 'STAFF';
      await downloadEdepExcel(reportType, selectedCampusId || undefined, selectedYearId || undefined, showCompleted);
    } catch (err) {
      setError('Erreur lors de l\'export Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      const reportType: EdepReportType = selectedCategory === 'STUDENTS' ? 'STUDENTS' :
                                          selectedCategory === 'TEACHERS' ? 'TEACHERS' : 'STAFF';
      await downloadEdepPdf(reportType, selectedCampusId || undefined, selectedYearId || undefined, showCompleted);
    } catch (err) {
      setError('Erreur lors de l\'export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // Physical documents export handlers - uses E-DEP data source for consistency
  const handlePhysicalExportExcel = async () => {
    try {
      setExportingPhysicalExcel(true);
      // Map category to E-DEP report type
      const reportType = physicalDocsCategory;

      const params = new URLSearchParams();
      params.set('reportType', reportType);
      if (selectedCampusId) params.set('campusId', selectedCampusId);
      if (selectedYearId) params.set('academicYearId', selectedYearId);
      params.set('includeComplete', String(showCompletePhysicalDocs)); // Include complete only when checkbox is checked

      // Use E-DEP physical documents export endpoint (same data source as dashboard)
      const response = await fetch(`${API_BASE}/reports/edep/physical-documents/export/excel?${params.toString()}`, {
        headers: {
          ...getAuthHeaders(),
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export Excel');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const typeLabel = physicalDocsCategory === 'STUDENTS' ? 'etudiants' :
                        physicalDocsCategory === 'TEACHERS' ? 'professeurs' : 'personnel';
      a.download = `documents-physiques-${typeLabel}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Erreur lors de l\'export Excel');
    } finally {
      setExportingPhysicalExcel(false);
    }
  };

  const handlePhysicalExportPdf = async () => {
    try {
      setExportingPhysicalPdf(true);
      // Map category to E-DEP report type
      const reportType = physicalDocsCategory;

      const params = new URLSearchParams();
      params.set('reportType', reportType);
      if (selectedCampusId) params.set('campusId', selectedCampusId);
      if (selectedYearId) params.set('academicYearId', selectedYearId);
      params.set('includeComplete', String(showCompletePhysicalDocs)); // Include complete only when checkbox is checked

      // Use E-DEP physical documents export endpoint (same data source as dashboard)
      const response = await fetch(`${API_BASE}/reports/edep/physical-documents/export/pdf?${params.toString()}`, {
        headers: {
          ...getAuthHeaders(),
          'Accept': 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const typeLabel = physicalDocsCategory === 'STUDENTS' ? 'etudiants' :
                        physicalDocsCategory === 'TEACHERS' ? 'professeurs' : 'personnel';
      a.download = `documents-physiques-${typeLabel}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Erreur lors de l\'export PDF');
    } finally {
      setExportingPhysicalPdf(false);
    }
  };

  // Format phone with country code
  const formatPhoneWithCountry = (phone: string | null, phoneCountry: string | null): string => {
    if (!phone) return '-';
    const countryCode = phoneCountry || '+212';
    if (phone.startsWith('+') || phone.startsWith('00')) {
      return phone;
    }
    const cleanPhone = phone.startsWith('0') ? phone.substring(1) : phone;
    return `${countryCode} ${cleanPhone}`;
  };

  // Get current report based on selected category
  const report = useMemo(() => {
    if (selectedCategory === 'STUDENTS') return studentsReport;
    if (selectedCategory === 'TEACHERS') return teachersReport;
    return staffReport;
  }, [selectedCategory, studentsReport, staffReport, teachersReport]);

  // Filter data for Profile tab
  const filteredData = useMemo(() => {
    if (!report?.data) return [];

    let data = [...report.data];

    if (!showCompleted) {
      data = data.filter((item) => !item.isComplete);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter((item) => {
        const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
        const email = item.email?.toLowerCase() || '';
        const code =
          selectedCategory === 'STUDENTS'
            ? (item as StudentProfileReport).studentCode?.toLowerCase() || ''
            : (item as StaffProfileReport).staffCode?.toLowerCase() || '';

        return fullName.includes(query) || email.includes(query) || code.includes(query);
      });
    }

    return data;
  }, [report?.data, showCompleted, searchQuery, selectedCategory]);

  const getCategoryLabel = (category: UserCategoryType) => {
    switch (category) {
      case 'STUDENTS': return 'Etudiants';
      case 'TEACHERS': return 'Professeurs';
      case 'STAFF': return 'Personnel';
      default: return category;
    }
  };

  // Filter physical docs data
  const filteredPhysicalDocsData = useMemo(() => {
    let data = [...physicalDocsData];

    // Filter by completion status
    if (!showCompletePhysicalDocs) {
      data = data.filter((item) => !item.isComplete);
    }

    // Filter by search
    if (physicalDocsSearch.trim()) {
      const query = physicalDocsSearch.toLowerCase();
      data = data.filter((item) => {
        const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
        const email = item.email?.toLowerCase() || '';
        const code = item.code?.toLowerCase() || '';
        return fullName.includes(query) || email.includes(query) || code.includes(query);
      });
    }

    return data;
  }, [physicalDocsData, showCompletePhysicalDocs, physicalDocsSearch]);

  // Get document columns for physical docs based on category
  const getPhysicalDocColumns = (category: 'STUDENTS' | 'STAFF' | 'TEACHERS') => {
    if (category === 'STUDENTS') {
      // Keys MUST match the student documents map built in loadPhysicalDocsData
      return [
        { key: 'CIN', label: 'CIN' },
        { key: 'BAC', label: 'BAC' },
        { key: 'RELEVE_BAC', label: 'Releve BAC' },
        { key: 'DERNIER_DIPLOME', label: 'Dernier Diplome' },
        { key: 'RELEVE_DIPLOME', label: 'Releve Diplome' },
        { key: 'RELEVE_ANNEES_PREC', label: 'Releves Annees Prec.' },
        { key: 'CV', label: 'CV' },
        { key: 'LETTRE_MOTIVATION', label: 'Lettre Motivation' },
      ];
    }
    // Teachers - includes Engagement des Professeurs + Autorisation (vacataires publics)
    if (category === 'TEACHERS') {
      return [
        { key: 'CIN_FRONT', label: 'CIN / Passeport' },
        { key: 'REGLEMENT_INTERIEUR_SIGNED', label: 'Reglement' },
        { key: 'CONSENTEMENT_SIGNED', label: 'Consentement' },
        { key: 'DIPLOME_SCAN', label: 'Diplome' },
        { key: 'CV', label: 'CV' },
        { key: 'RIB', label: 'RIB' },
        { key: 'CONTRAT_TRAVAIL', label: 'Contrat' },
        { key: 'ENGAGEMENT_PROFESSEUR', label: 'Engagement' },
        { key: 'AUTORISATION_ENSEIGNER', label: 'Autorisation' },
      ];
    }
    // Staff - without Engagement des Professeurs
    return [
      { key: 'CIN_FRONT', label: 'CIN / Passeport' },
      { key: 'REGLEMENT_INTERIEUR_SIGNED', label: 'Reglement' },
      { key: 'CONSENTEMENT_SIGNED', label: 'Consentement' },
      { key: 'DIPLOME_SCAN', label: 'Diplome' },
      { key: 'CV', label: 'CV' },
      { key: 'RIB', label: 'RIB' },
      { key: 'CONTRAT_TRAVAIL', label: 'Contrat' },
    ];
  };

  const getCategoryIcon = (category: UserCategoryType) => {
    switch (category) {
      case 'STUDENTS': return <GraduationCap className="w-5 h-5" />;
      case 'TEACHERS': return <BookOpen className="w-5 h-5" />;
      case 'STAFF': return <UserCog className="w-5 h-5" />;
      default: return <Users className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Check if user has at least one permission to view this page
  if (!canViewProfiles && !canViewPhysicalDocs) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Acces non autorise</h2>
        <p className="text-gray-500">Vous n'avez pas les permissions necessaires pour acceder a cette page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suivi des Dossiers</h1>
          <p className="text-gray-500 mt-1">
            Suivez la completion des profils et la reception des documents physiques
          </p>
          {/* The year being consulted, stated up front: every figure on this page
              describes it, and a past year is a different answer from today's. */}
          {selectedYearName && (
            <p className="text-sm font-medium text-gray-700 mt-2">
              Année académique&nbsp;:{' '}
              <span className="text-primary-700">{selectedYearName}</span>
              {!isViewingCurrentYear && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                  <Clock className="w-3 h-3" />
                  Année passée — état de cette année-là
                </span>
              )}
            </p>
          )}
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Alerts */}
      {missingCurrentYear && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              L'année {missingCurrentYear} n'existe pas encore
            </p>
            <p className="text-sm text-amber-700">
              D'après les paramètres de l'année académique, nous sommes en{' '}
              {missingCurrentYear}, mais cette année n'a pas encore été créée. Le suivi
              ci-dessous porte donc sur l'année précédente. Créez-la dans Paramètres →
              gestion du planning pour suivre l'année en cours.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">Succes</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-5 h-5 text-green-600" />
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {canViewProfiles && (
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'profile'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Eye className="w-4 h-4" />
                Suivi des documents — version scannée
              </button>
            )}
            {canViewPhysicalDocs && (
              <button
                onClick={() => setActiveTab('physical')}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'physical'
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileInput className="w-4 h-4" />
                Suivi des documents — version physique
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* ============== PROFILE TAB ============== */}
          {activeTab === 'profile' && canViewProfiles && (
            <div className="space-y-6">
              {/* Category Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(['STUDENTS', 'STAFF', 'TEACHERS'] as UserCategoryType[]).map((category) => {
                  const stats = category === 'STUDENTS' ? summary?.students :
                                category === 'TEACHERS' ? summary?.teachers : summary?.staff;
                  const isSelected = selectedCategory === category;

                  return (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`bg-white rounded-xl border-2 p-4 text-left transition-all ${
                        isSelected ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${
                          category === 'STUDENTS' ? 'bg-blue-100' :
                          category === 'TEACHERS' ? 'bg-purple-100' : 'bg-emerald-100'
                        }`}>
                          <span className={
                            category === 'STUDENTS' ? 'text-blue-600' :
                            category === 'TEACHERS' ? 'text-purple-600' : 'text-emerald-600'
                          }>
                            {getCategoryIcon(category)}
                          </span>
                        </div>
                        <span className="font-semibold text-gray-900">{getCategoryLabel(category)}</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{stats?.total || 0} profils</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span className="text-green-600">{stats?.complete || 0} complets</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          <span className="text-amber-600">{stats?.incomplete || 0} incomplets</span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all"
                            style={{ width: `${stats?.completePercentage || 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{stats?.completePercentage || 0}% de progression</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Année académique
                  </label>
                  <select
                    value={selectedYearId}
                    onChange={(e) => setSelectedYearId(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {academicYears.map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.name}
                        {year.id === currentYearId ? ' (année en cours)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {campuses.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={selectedCampusId}
                      onChange={(e) => setSelectedCampusId(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Tous les campus</option>
                      {campuses.map((campus) => (
                        <option key={campus.id} value={campus.id}>
                          {campus.name} ({campus.city})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex-1 min-w-[200px] relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom, email ou code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Afficher les profils complets</span>
                </label>

                {canExportProfiles && (
                  <div className="flex items-center gap-3 ml-auto">
                    <span className="text-sm text-gray-500 font-medium">Exporter:</span>
                    <button
                      onClick={handleExportExcel}
                      disabled={exportingExcel || loadingTable}
                      className="flex items-center gap-2 px-4 py-2.5 text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {exportingExcel ? <RefreshCw className="w-5 h-5 animate-spin" /> : <FileSpreadsheet className="w-5 h-5" />}
                      Excel
                    </button>
                    <button
                      onClick={handleExportPdf}
                      disabled={exportingPdf || loadingTable}
                      className="flex items-center gap-2 px-4 py-2.5 text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {exportingPdf ? <RefreshCw className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                      PDF
                    </button>
                  </div>
                )}
              </div>

              {/* Data Table */}
              {loadingTable ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {report?.stats.byField && report.stats.byField.length > 6 && (
                    <div className="px-4 py-2 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-600 flex items-center gap-2">
                      <span className="text-blue-500">Info:</span>
                      <span>Faites defiler horizontalement pour voir tous les champs obligatoires</span>
                    </div>
                  )}
                  <div className="overflow-x-auto border border-gray-200 rounded-lg" style={{ maxHeight: '60vh' }}>
                    <table className="w-full min-w-max">
                      <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap sticky left-0 bg-gradient-to-r from-blue-600 to-blue-600 z-20">
                            {selectedCategory === 'STUDENTS' ? 'Code Etudiant' : 'Code Personnel'}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Nom & Prenom
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Telephone
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Statut
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Progression
                          </th>
                          {report?.stats.byField.map((field) => (
                            <th
                              key={field.field}
                              className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider align-bottom"
                              title={field.label}
                            >
                              <div className="flex flex-col items-center gap-1">
                                <span className="whitespace-normal break-words leading-tight max-w-[120px]">
                                  {field.label}
                                </span>
                                <span className="text-[10px] text-white/70 font-normal">
                                  {field.completionPercentage}%
                                </span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {filteredData.length === 0 ? (
                          <tr>
                            <td colSpan={(report?.stats.byField.length || 0) + 6} className="px-4 py-8 text-center text-gray-500">
                              {searchQuery || !showCompleted
                                ? 'Aucun profil incomplet trouve - Tous les profils sont complets !'
                                : 'Aucune donnee disponible'}
                            </td>
                          </tr>
                        ) : (
                          filteredData.map((item) => {
                            const isStudent = selectedCategory === 'STUDENTS';
                            const student = item as StudentProfileReport;
                            const staffOrTeacher = item as StaffProfileReport | TeacherProfileReport;

                            return (
                              <tr
                                key={item.id}
                                className={`hover:bg-gray-50 transition-colors ${!item.isComplete ? 'bg-amber-50/30' : ''}`}
                              >
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white z-10">
                                  {isStudent ? student.studentCode : staffOrTeacher.staffCode || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <span>{item.lastName} {item.firstName}</span>
                                    {isStudent && student.isAlsoStaff && (
                                      <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">+ Staff</span>
                                    )}
                                    {!isStudent && staffOrTeacher.isAlsoProfessor && (
                                      <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">+ Enseignant</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                  {item.email || <span className="text-gray-400">-</span>}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap font-mono">
                                  {formatPhoneWithCountry(item.phone, (item as any).phoneCountry)}
                                </td>
                                <td className="px-4 py-3 text-center whitespace-nowrap">
                                  {item.isComplete ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                      <CheckCircle2 className="w-3 h-3" />
                                      Complet
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                                      <AlertTriangle className="w-3 h-3" />
                                      Incomplet
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-20 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full transition-all ${
                                          item.completionPercentage === 100
                                            ? 'bg-gradient-to-r from-green-400 to-green-500'
                                            : item.completionPercentage >= 50
                                            ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                                            : 'bg-gradient-to-r from-red-400 to-red-500'
                                        }`}
                                        style={{ width: `${item.completionPercentage}%` }}
                                      />
                                    </div>
                                    <span className={`text-xs font-medium w-10 ${
                                      item.completionPercentage === 100 ? 'text-green-600' :
                                      item.completionPercentage >= 50 ? 'text-amber-600' : 'text-red-600'
                                    }`}>
                                      {item.completionPercentage}%
                                    </span>
                                  </div>
                                </td>
                                {item.fields.map((field) => (
                                  <td key={field.field} className="px-3 py-3 text-center">
                                    {field.completed ? (
                                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                                    ) : (
                                      <X className="w-5 h-5 text-red-400 mx-auto" />
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredData.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="font-medium">
                          {filteredData.length} profil{filteredData.length > 1 ? 's' : ''} {!showCompleted ? 'incomplet' : ''}{filteredData.length > 1 && !showCompleted ? 's' : ''}
                        </span>
                        <span className="text-gray-400">|</span>
                        <span>sur {report?.stats.total || 0} au total</span>
                      </div>
                      {summary && (
                        <span className="flex items-center gap-2 text-gray-400">
                          <Clock className="w-4 h-4" />
                          Derniere mise a jour: {new Date(summary.generatedAt).toLocaleString('fr-FR')}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ============== PHYSICAL DOCUMENTS TAB ============== */}
          {activeTab === 'physical' && canViewPhysicalDocs && (
            <div className="space-y-6">
              {/* Category Selection with Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(['STUDENTS', 'STAFF', 'TEACHERS'] as const).map((category) => {
                  const statsKey = category === 'STUDENTS' ? 'students' : category === 'TEACHERS' ? 'teachers' : 'staff';
                  const stats = physicalDocsSummary?.[statsKey];
                  const isSelected = physicalDocsCategory === category;

                  return (
                    <button
                      key={category}
                      onClick={() => setPhysicalDocsCategory(category)}
                      className={`bg-white rounded-xl border-2 p-4 text-left transition-all ${
                        isSelected ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg ${
                          category === 'STUDENTS' ? 'bg-blue-100' :
                          category === 'TEACHERS' ? 'bg-purple-100' : 'bg-emerald-100'
                        }`}>
                          <span className={
                            category === 'STUDENTS' ? 'text-blue-600' :
                            category === 'TEACHERS' ? 'text-purple-600' : 'text-emerald-600'
                          }>
                            {getCategoryIcon(category)}
                          </span>
                        </div>
                        <span className="font-semibold text-gray-900">{getCategoryLabel(category)}</span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900">{stats?.total || 0} dossiers</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span className="text-green-600">{stats?.complete || 0} complets</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          <span className="text-amber-600">{stats?.incomplete || 0} incomplets</span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all"
                            style={{ width: `${stats?.completePercentage || 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{stats?.completePercentage || 0}% de progression</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Année académique
                  </label>
                  <select
                    value={selectedYearId}
                    onChange={(e) => setSelectedYearId(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {academicYears.map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.name}
                        {year.id === currentYearId ? ' (année en cours)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {campuses.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={selectedCampusId}
                      onChange={(e) => setSelectedCampusId(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Tous les campus</option>
                      {campuses.map((campus) => (
                        <option key={campus.id} value={campus.id}>
                          {campus.name} ({campus.city})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex-1 min-w-[200px] relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par nom, email ou code..."
                    value={physicalDocsSearch}
                    onChange={(e) => setPhysicalDocsSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {physicalDocsSearch && (
                    <button
                      onClick={() => setPhysicalDocsSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showCompletePhysicalDocs}
                    onChange={(e) => setShowCompletePhysicalDocs(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Afficher les dossiers complets</span>
                </label>

                {canExportPhysicalDocs && (
                  <div className="flex items-center gap-3 ml-auto">
                    <span className="text-sm text-gray-500 font-medium">Exporter:</span>
                    <button
                      onClick={handlePhysicalExportExcel}
                      disabled={exportingPhysicalExcel || loadingPhysicalDocs}
                      className="flex items-center gap-2 px-4 py-2.5 text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {exportingPhysicalExcel ? <RefreshCw className="w-5 h-5 animate-spin" /> : <FileSpreadsheet className="w-5 h-5" />}
                      Excel
                    </button>
                    <button
                      onClick={handlePhysicalExportPdf}
                      disabled={exportingPhysicalPdf || loadingPhysicalDocs}
                      className="flex items-center gap-2 px-4 py-2.5 text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {exportingPhysicalPdf ? <RefreshCw className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                      PDF
                    </button>
                  </div>
                )}
              </div>

              {/* Data Table */}
              {loadingPhysicalDocs ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto border border-gray-200 rounded-lg" style={{ maxHeight: '60vh' }}>
                    <table className="w-full min-w-max">
                      <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap sticky left-0 bg-gradient-to-r from-blue-600 to-blue-600 z-20">
                            Code
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Nom & Prenom
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Telephone
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Statut
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap">
                            Progression
                          </th>
                          {/* Document columns based on category */}
                          {getPhysicalDocColumns(physicalDocsCategory).map((col) => (
                            <th
                              key={col.key}
                              className="px-3 py-3 text-center text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                            >
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {filteredPhysicalDocsData.length === 0 ? (
                          <tr>
                            <td colSpan={6 + getPhysicalDocColumns(physicalDocsCategory).length} className="px-4 py-8 text-center text-gray-500">
                              {physicalDocsSearch || !showCompletePhysicalDocs
                                ? 'Aucun dossier incomplet trouve - Tous les dossiers sont complets !'
                                : 'Aucune donnee disponible'}
                            </td>
                          </tr>
                        ) : (
                          filteredPhysicalDocsData.map((item) => (
                            <tr
                              key={item.id}
                              className={`hover:bg-gray-50 transition-colors ${!item.isComplete ? 'bg-amber-50/30' : ''}`}
                            >
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white z-10">
                                {item.code || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                {`${item.lastName} ${item.firstName}`.trim() || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                                {item.email || <span className="text-gray-400">-</span>}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap font-mono">
                                {item.phone ? formatPhoneWithCountry(item.phone, item.phoneCountry) : <span className="text-gray-400">-</span>}
                              </td>
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                {item.isComplete ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Complet
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                                    <AlertTriangle className="w-3 h-3" />
                                    Incomplet
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-20 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full transition-all ${
                                        item.percentageComplete === 100
                                          ? 'bg-gradient-to-r from-green-400 to-green-500'
                                          : item.percentageComplete >= 50
                                          ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                                          : 'bg-gradient-to-r from-red-400 to-red-500'
                                      }`}
                                      style={{ width: `${item.percentageComplete}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs font-medium w-10 ${
                                    item.percentageComplete === 100 ? 'text-green-600' :
                                    item.percentageComplete >= 50 ? 'text-amber-600' : 'text-red-600'
                                  }`}>
                                    {item.percentageComplete}%
                                  </span>
                                </div>
                              </td>
                              {/* Document checkboxes */}
                              {getPhysicalDocColumns(physicalDocsCategory).map((col) => {
                                const docInfo = item.documents?.[col.key];
                                const isReceived = docInfo?.isReceived || false;
                                const isUpdating = updatingDocId === `${item.userId}-${col.key}`;

                                // Document not applicable to this person (e.g. conditional student docs)
                                if (!docInfo) {
                                  return (
                                    <td key={col.key} className="px-3 py-3 text-center">
                                      <span className="text-gray-300">-</span>
                                    </td>
                                  );
                                }

                                return (
                                  <td key={col.key} className="px-3 py-3 text-center">
                                    <button
                                      onClick={() => togglePhysicalDoc(item.userId, col.key, !isReceived)}
                                      disabled={isUpdating}
                                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                                    >
                                      {isUpdating ? (
                                        <Loader2 className="w-5 h-5 text-gray-400 animate-spin mx-auto" />
                                      ) : isReceived ? (
                                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                                      ) : (
                                        <X className="w-5 h-5 text-red-400 mx-auto" />
                                      )}
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredPhysicalDocsData.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="font-medium">
                          {filteredPhysicalDocsData.length} dossier{filteredPhysicalDocsData.length > 1 ? 's' : ''} {!showCompletePhysicalDocs ? 'incomplet' : ''}{filteredPhysicalDocsData.length > 1 && !showCompletePhysicalDocs ? 's' : ''}
                        </span>
                        <span className="text-gray-400">|</span>
                        <span>sur {physicalDocsData.length} au total</span>
                      </div>
                      <span className="flex items-center gap-2 text-gray-400">
                        <Clock className="w-4 h-4" />
                        Derniere mise a jour: {new Date().toLocaleString('fr-FR')}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuiviDossiersPage;
